<template>
  <div class="mt-2">
    <!-- Booking Ref top -->
    <div class="row g-2 mb-2" v-if="flight['Book Ref']">
      <div class="col-sm-6 col-md-3">
        <div class="text-muted small">{{ __DICT__.bookingRefShort }}</div>
        <div class="fw-medium">{{ flight['Book Ref'] }}</div>
      </div>
    </div>

    <!-- Passenger/basic info -->
    <div class="row g-2 mb-2">
      <div class="col-sm-6 col-md-3">
        <div class="text-muted small">{{ __DICT__.flight.passenger }}</div>
        <div class="fw-medium">{{ showOrDash(flight['First Passport']) }} {{ showOrDash(flight['Last Passport']) }}</div>
      </div>
      <div class="col-sm-6 col-md-3" v-if="flight['Date of Birth']">
        <div class="text-muted small">{{ __DICT__.flight.dob }}</div>
        <div class="fw-medium">{{ flight['Date of Birth'] }}</div>
      </div>
      <div class="col-sm-6 col-md-3" v-if="flight['Nationality']">
        <div class="text-muted small">{{ __DICT__.nationality }}</div>
        <div class="fw-medium">{{ flight['Nationality'] }}</div>
      </div>
    </div>

    <!-- Journey details: Outbound -->
    <Card v-if="hasOB" size="sm" margin="mb-2" :title="__DICT__.outbound" body-class="py-2">
        <div class="row g-2">
          <div class="col-md-3" v-if="flight['OB Flight No']">
            <div class="text-muted small">{{ __DICT__.flight.flightNo }}</div>
            <div class="fw-medium">{{ flight['OB Flight No'] }}</div>
          </div>
          <div class="col-md-3" v-if="flight['OB Flight Date']">
            <div class="text-muted small">{{ __DICT__.date }}</div>
            <div class="fw-medium">{{ flight['OB Flight Date'] }}</div>
          </div>
          <div class="col-md-3" v-if="flight['OB Route']">
            <div class="text-muted small">{{ __DICT__.route }}</div>
            <div class="fw-medium">{{ flight['OB Route'] }}</div>
          </div>
          <div class="col-md-3" v-if="flight['OB  TO'] || flight['OB  LD']">
            <div class="text-muted small">{{ __DICT__.flight.times }}</div>
            <div class="fw-medium">
              <span v-if="flight['OB  TO']">{{ __DICT__.flight.takeOff }} {{ flight['OB  TO'] }}</span>
              <span v-if="flight['OB  TO'] && flight['OB  LD']"> • </span>
              <span v-if="flight['OB  LD']">{{ __DICT__.flight.landing }} {{ flight['OB  LD'] }}</span>
            </div>
          </div>
        </div>
    </Card>

    <!-- Journey details: Inbound -->
    <Card v-if="hasIB" size="sm" margin="mb-2" :title="__DICT__.inbound" body-class="py-2">
        <div class="row g-2">
          <div class="col-md-3" v-if="flight['IB Flight No']">
            <div class="text-muted small">{{ __DICT__.flight.flightNo }}</div>
            <div class="fw-medium">{{ flight['IB Flight No'] }}</div>
          </div>
          <div class="col-md-3" v-if="flight['IB Flight Date']">
            <div class="text-muted small">{{ __DICT__.date }}</div>
            <div class="fw-medium">{{ flight['IB Flight Date'] }}</div>
          </div>
          <div class="col-md-3" v-if="flight['IB Route']">
            <div class="text-muted small">{{ __DICT__.route }}</div>
            <div class="fw-medium">{{ flight['IB Route'] }}</div>
          </div>
          <div class="col-md-3" v-if="flight['IB  TO'] || flight['IB  LD']">
            <div class="text-muted small">{{ __DICT__.flight.times }}</div>
            <div class="fw-medium">
              <span v-if="flight['IB  TO']">{{ __DICT__.flight.takeOff }} {{ flight['IB  TO'] }}</span>
              <span v-if="flight['IB  TO'] && flight['IB  LD']"> • </span>
              <span v-if="flight['IB  LD']">{{ __DICT__.flight.landing }} {{ flight['IB  LD'] }}</span>
            </div>
          </div>
        </div>
    </Card>

    <!-- Luggage and extras -->
    <div class="row g-2 mb-2">
      <div class="col-md-3" v-if="flight['Hand Luggage']">
        <div class="text-muted small">{{ __DICT__.flight.handLuggage }}</div>
        <div class="fw-medium">{{ flight['Hand Luggage'] }}</div>
      </div>
      <div class="col-md-3" v-if="flight['Hold Luggage']">
        <div class="text-muted small">{{ __DICT__.flight.holdLuggage }}</div>
        <div class="fw-medium">{{ flight['Hold Luggage'] }}</div>
      </div>
      <div class="col-md-3" v-if="flight['Hold Weight (KGS)']">
        <div class="text-muted small">{{ __DICT__.flight.holdWeight }}</div>
        <div class="fw-medium">{{ flight['Hold Weight (KGS)'] }}</div>
      </div>
    </div>

    <!-- Supplier / Booking -->
    <div class="row g-2 mb-2">
      <div class="col-md-3" v-if="flight['Supplier']">
        <div class="text-muted small">{{ __DICT__.supplier }}</div>
        <div class="fw-medium">{{ flight['Supplier'] }}</div>
      </div>
      <div class="col-md-3" v-if="flight['Book Ref']">
        <div class="text-muted small">{{ __DICT__.bookingRefShort }}</div>
        <div class="fw-medium">{{ flight['Book Ref'] }}</div>
      </div>
      <div class="col-md-3" v-if="flight['Luggage Preference']">
        <div class="text-muted small">{{ __DICT__.flight.luggagePreference }}</div>
        <div class="fw-medium">{{ flight['Luggage Preference'] }}</div>
      </div>
    </div>

    <!-- Long-form info -->
    <div class="mb-2" v-if="flight['Information Required For Check-in']">
      <div class="text-muted small">{{ __DICT__.flight.infoRequiredForCheckIn }}</div>
      <div class="">{{ flight['Information Required For Check-in'] }}</div>
    </div>

    <div class="mb-2" v-if="flight['Notes']">
      <div class="text-muted small">{{ __DICT__.notes }}</div>
      <div class="">{{ flight['Notes'] }}</div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { Card } from "@/components/atomic";

const props = defineProps({
  flight: { type: Object, required: true },
})

const hasOB = computed(() => {
  const f = props.flight || {}
  // Show Outbound box only if there is a flight number
  const num = (f['OB Flight No'] || '').toString().trim()
  return num.length > 0
})

const hasIB = computed(() => {
  const f = props.flight || {}
  // Show Inbound box only if there is a flight number
  const num = (f['IB Flight No'] || '').toString().trim()
  return num.length > 0
})

function showOrDash(v) {
  if (v === null || v === undefined) return '—'
  const s = String(v).trim()
  return s.length ? s : '—'
}
</script>
